<template>
  <b-overlay :show="loading" variant="white" :opacity="0.9">
    <validation-observer ref="guestFormRules">
      <b-form id="guest-form">
        <!-- GUEST INFORMATION -->
        <b-row class="mb-5">
          <b-col cols="12">
            <h5 class="mb-3">
              {{ $t("Personal information") }}
            </h5>
          </b-col>
          <!-- FIRST NAME -->
          <b-col cols="12">
            <b-form-group :label="$t('First name')" label-for="guest-first-name">
              <validation-provider #default="{ errors }" name="firstName" rules="required">
                <b-form-input id="guest-first-name" v-model="newGuest.firstName"
                  :state="errors.length > 0 ? false : null" trim />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <!-- LAST NAME -->
          <b-col cols="12">
            <b-form-group :label="$t('Last name')" label-for="guest-last-name">
              <validation-provider #default="{ errors }" name="lastName"
                :rules="newGuest.idType === 'NIF' ? 'required|twolastnames' : 'required'">
                <b-form-input id="guest-last-name" v-model="newGuest.lastName" :state="errors.length > 0 ? false : null"
                  trim />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <!-- BIRTHDATE -->
          <b-col cols="12">
            <label for="guest-birthday">
              {{ $t("Birthdate") }}
            </label>
            <validation-provider #default="{ errors }" name="birthdate" rules="required|pastdate">
              <b-datepicker v-model="newGuest.birthDate" :max="new Date()" :min="birthDateMin"
                :state="errors.length > 0 ? false : null" />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-col>
        </b-row>

        <!-- ADDRESS INFORMATION -->
        <b-row class="mb-5">
          <b-col cols="12">
            <h5 class="mb-3">
              {{ $t("Address information") }}
            </h5>
          </b-col>
          <!-- ADDRESS COUNTRY -->
          <b-col cols="12" class="mt-2">
            <b-form-group :label="$t('Country')" label-for="guest-address-country">
              <validation-provider #default="{ errors }" name="country" rules="required">
                <v-select id="guest-address-country" v-model="newGuest.addressCountry" :options="countryOptionsAlpha3"
                  :reduce="option => option.value" class="w-full" :class="{ 'is-invalid': errors.length > 0 }" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <!-- ADDRESS ZIP SELECT -->
          <b-col v-if="newGuest.addressCountry === 'ESP'" cols="12" class="mt-2">
            <b-form-group :label="$t('Postal code')" label-for="guest-address-zip-select">
              <validation-provider #default="{ errors }" name="postalCode"
                :rules="newGuest.addressCountry === 'ESP' ? 'required' : ''">
                <v-select id="guest-address-zip-select" v-model="newGuest.addressZipInput" label="zipCode"
                  :filterable="false" :options="spainCityZipOptions" :disabled="!newGuest.addressCountry" class="w-full"
                  :class="{ 'is-invalid': errors.length > 0 }" @search="onSearchCityZip" @input="autoFillCityInfo">
                  <template slot="no-options">
                    <span class="text-muted">{{$t('Postal code search placeholder')}}</span>
                  </template>
                  <template slot="option" slot-scope="option">
                    <div>
                      <span>{{ option.zipCode }}</span>
                      <span class="ml-1">{{ option.cityName }}</span>
                    </div>
                  </template>
                </v-select>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <!-- ADDRESS ZIP TEXT INPUT -->
          <b-col v-else cols="12" class="mt-2">
            <b-form-group :label="$t('Postal code')" label-for="guest-address-zip">
              <validation-provider #default="{ errors }" name="postalCode"
                :rules="newGuest.addressCountry !== 'ESP' ? 'required' : ''">
                <b-form-input id="guest-address-zip" v-model="newGuest.addressZip" trim
                  :state="errors.length > 0 ? false : null" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <!-- ADDRESS CITY -->
          <b-col cols="12" class="mt-2">
            <b-form-group :label="$t('City')" label-for="guest-address-city">
              <validation-provider #default="{ errors }" name="city" rules="required">
                <b-form-input id="guest-address-city" v-model="newGuest.addressCityName" trim
                  :disabled="!newGuest.addressZip" :state="errors.length > 0 ? false : null" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <!-- ADDRESS NAME -->
          <b-col cols="12" class="mt-2">
            <b-form-group :label="$t('Address')" label-for="guest-address">
              <validation-provider #default="{ errors }" name="address" rules="required">
                <b-form-input id="guest-address" v-model="newGuest.address" trim :disabled="!newGuest.addressZip"
                  :state="errors.length > 0 ? false : null" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- ID INFORMATION -->
        <b-row v-if="askForIdInformation" class="mb-4">
          <b-col cols="12">
            <h5 class="mb-3">
              {{ $t("Identification") }}
            </h5>
          </b-col>
          <!-- ID TYPE -->
          <b-col cols="12">
            <label for="guest-id-type">
              {{ $t("ID type") }}
            </label>
            <b-form-group>
              <validation-provider #default="{ errors }" name="idType" :rules="askForIdInformation ? 'required' : ''">
                <v-select id="guest-country" v-model="newGuest.idType" :options="idTypeOptions"
                  :reduce="option => option.value" class="w-full" :class="{ 'is-invalid': errors.length > 0 }"
                  :disabled="!askForIdInformation" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <!-- ID NUMBER -->
          <b-col cols="12">
            <b-form-group :label="$t('ID number')" label-for="guest-id-number">
              <validation-provider #default="{ errors }" name="idNumber" :rules="idNumberValidationRules">
                <b-form-input id="guest-id-number" v-model="newGuest.idNumber" trim
                  :formatter="value => value.toUpperCase()" :state="errors.length > 0 ? false : null"
                  :disabled="!askForIdInformation" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <!-- ID SUPPORT NUMBER -->
          <b-col v-if="newGuest.idType === 'NIF' || newGuest.idType === 'NIE'" cols="12">
            <b-form-group :label="$t('ID support number')" label-for="guest-id-support-number">
              <validation-provider #default="{ errors }" name="idSupportNumber" :rules="idSupportNumberValidationRules">
                <b-form-input id="guest-id-support-number" v-model="newGuest.idSupportNumber" trim
                  :formatter="value => value.toUpperCase()" :state="errors.length > 0 ? false : null" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- SIGNATURE -->
        <b-row v-if="askForIdInformation" class="mb-4">
          <b-col cols="12" class="mb-1">
            <label for="guest-signature">
              {{ $t("Signature") }}
              <small class="cursor-pointer text-primary ml-25" @click="clearSignature()">
                {{ $t("pages.booking.deleteSignature") }}
              </small>
            </label>
          </b-col>
          <b-col v-if="guest && newGuest.signature" cols="12">
            <b-img id="guest-signature" class="guest-signature mb-1" :src="newGuest.signature" alt="" fluid center />
          </b-col>
          <b-col v-else cols="12">
            <vue-signature-pad id="signature-pad" ref="signaturePad" width="100%" height="250px"
              class="mb-1 cursor-pointer" :class="{ 'is-invalid': signaturePadError }" :options="signaturePadOptions" />
          </b-col>
        </b-row>

        <!-- BUTTONS -->
        <b-row class="mb-4 d-flex justify-content-between justify-content-sm-end">
          <b-col cols="12" sm="auto">
            <b-button block variant="primary" @click="save()">
              {{ $t("Save") }}
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>
  </b-overlay>
</template>

<script>
import {
  BOverlay,
  BRow,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BButton,
  BImg
} from "bootstrap-vue";
import vSelect from "vue-select";
import {
  countryOptionsAlpha3,
  idTypeOptions
} from "@/utils/select-options";
import VueSignaturePad from "vue-signature-pad";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, pastdate, twolastnames, dni, nie, cif, soportedni, soportenie } from "@foravila-validations";
import BDatepicker from "@foravila-core/components/b-datepicker/BDatepicker.vue";
import { formatDateObjectToDatabaseDateTime } from "@/utils/formatters";
import axios from "@/axios";
import _debounce from "lodash/debounce";

export default {
  components: {
    BOverlay,
    vSelect,
    BForm,
    BFormGroup,
    BFormInput,
    BRow,
    BCol,
    BButton,
    BImg,
    VueSignaturePad,
    ValidationProvider,
    ValidationObserver,
    BDatepicker
  },
  props: {
    guest: {
      type: Object,
      default: null
    },
    isBaby: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      loading: false,
      newGuest: null,
      signaturePadOptions: {
        penColor: "#000",
        onBegin: () => {
          if (this.$refs.signaturePad.isEmpty())
            this.$refs.signaturePad.resizeCanvas();
        }
      },
      signaturePadError: null,
      required,
      pastdate,
      twolastnames,
      dni,
      nie,
      cif,
      soportedni,
      soportenie,
      spainCityZipOptions: [],
    };
  },
  computed: {
    booking() {
      return this.$store.getters["booking/booking"];
    },
    countryOptionsAlpha3() {
      return countryOptionsAlpha3(this.$i18n.locale);
    },
    idTypeOptions() {
      return idTypeOptions(this.$i18n.locale).filter(option => option.value !== "CIF" && option.value !== "DNI");
    },
    isStacked() {
      const currentBreakpoint = this.$store.getters["app/currentBreakPoint"];
      return currentBreakpoint === "xs" || currentBreakpoint === "sm" || false;
    },
    newGuestAge() {
      if (!this.newGuest.birthDate) return null;
      const birthDate = new Date(this.newGuest.birthDate);
      const ageDifMs = Date.now() - birthDate.getTime();
      const ageDate = new Date(ageDifMs);
      return Math.abs(ageDate.getUTCFullYear() - 1970);
    },
    askForIdInformation() {
      return this.newGuestAge >= 18;
    },
    birthDateMin() {
      if (this.isBaby) {
        const date = new Date();
        date.setFullYear(date.getFullYear() - 2);
        date.setDate(date.getDate() + 1);
        return date;
      }
      return null;
    },
    idNumberValidationRules() {
      if (!this.askForIdInformation) {
        return "";
      }

      const rules = ["required"];

      if (this.newGuest.idType === "NIF") {
        rules.push("dni")
      }

      if (this.newGuest.idType === "NIE") {
        rules.push("nie")
      }

      return rules.length ? rules.join("|") : "";
    },
    idSupportNumberValidationRules() {
      if (!this.askForIdInformation) {
        return "";
      }

      if (this.newGuest.idType === "NIF") {
        return "required|soportedni";
      }

      if (this.newGuest.idType === "NIE") {
        return "required|soportenie";
      }

      return "";
    }
  },
  created() {
    this.init();
  },
  methods: {
    init() {
      this.reset();
      if (!this.guest) return;
      this.newGuest = {
        firstName: this.guest.firstName || null,
        lastName: this.guest.lastName || null,
        birthDate: this.guest.birthDate || null,
        idType: this.guest.idType || null,
        idNumber: this.guest.idNumber || null,
        idSupportNumber: this.guest.idSupportNumber || null,
        address: this.guest.address || null,
        addressCountry: this.guest.addressCountry || null,
        addressZip: this.guest.addressZip || null,
        addressZipInput: this.guest.addressZip || null,
        addressCityCode: this.guest.addressCityCode || null,
        addressCityName: this.guest.addressCityName || null,
        signature: this.guest.signature || null,
      };
    },
    reset() {
      this.newGuest = {
        firstName: null,
        lastName: null,
        birthDate: null,
        idType: null,
        idNumber: null,
        idSupportNumber: null,
        address: null,
        addressCountry: null,
        addressZip: null,
        addressZipInput: null,
        addressCityCode: null,
        addressCityName: null,
        signature: null,
      };
    },
    clearSignature() {
      this.newGuest.signature = null;
      if (this.$refs.signaturePad) this.$refs.signaturePad.clearSignature();
      this.validateSignature();
    },
    cancel() {
      this.$emit("cancel");
    },
    save() {
      const validSignature = this.validateSignature();
      this.$refs.guestFormRules.validate().then(success => {
        if (success && validSignature) {
          if (this.guest?.uuid) this.editGuest();
          else this.createGuest();
        }
      });
    },
    createGuest() {
      try {
        this.setLoading(true);

        if (this.$refs.signaturePad) {
          const { data } = this.$refs.signaturePad.saveSignature();
          this.newGuest.signature = data || null;
        }

        // TODO: improve this time delay. It is set here to give time to the signature pad to process the data
        setTimeout(() => {
          this.$store
            .dispatch("booking/addGuest", {
              booking: this.booking["@id"],
              ...this.newGuest,
              source: 'INTRANET_GUESTS',
              confirmed: true,
              confirmedDate: formatDateObjectToDatabaseDateTime(new Date())
            })
            .then(() => this.$emit("guest-added"))
            .catch(() => {
              this.$emit("add-guest-error");
            })
            .finally(() => {
              this.setLoading(false);
            });
        }, 200);
      } catch (error) {
        this.setLoading(false);
      }
    },
    editGuest() {
      try {
        this.setLoading(true);

        if (this.$refs.signaturePad) {
          const { data } = this.$refs.signaturePad.saveSignature();
          this.newGuest.signature = data || null;
        }

        // TODO: improve this time delay. It is set here to give time to the signature pad to process the data
        setTimeout(() => {
          this.$store
            .dispatch("booking/updateGuest", {
              uuid: this.guest.uuid,
              ...this.newGuest
            })
            .then(() => this.$emit("guest-edited"))
            .catch(() => {
              this.$emit("edit-guest-error");
            })
            .finally(() => {
              this.setLoading(false);
            });
        }, 200);
      } catch (error) {
        this.setLoading(false);
      }
    },
    setLoading(loading) {
      this.loading = loading;
      this.$emit("loading", loading);
    },
    validateSignature() {
      if (!this.$refs.signaturePad) {
        this.signaturePadError = null;
        return true;
      }
      if (this.$refs.signaturePad.isEmpty()) {
        this.signaturePadError = true;
        return false;
      }
      this.signaturePadError = false;
      return true;
    },
    onSearchCityZip(search, loading) {
      if (search.length) {
        loading(true);
        this.fetchSpainCityZipOptions(search, loading, this);
      }
    },
    fetchSpainCityZipOptions: _debounce((search, loading, innerThis) => {
      axios
        .get(`/spain_zip_codes?zipCode=${search}`)
        .then(response => {
          const results = response.data["hydra:member"] || [];
          innerThis.spainCityZipOptions = results.sort((a, b) =>
            a.zipCode.localeCompare(b.zipCode)
          );
        })
        .catch(() => {
          innerThis.spainCityZipOptions = [];
        })
        .finally(() => loading(false));
    }, 1000),
    autoFillCityInfo(spainCity) {
      this.newGuest.addressZip = spainCity?.zipCode || null;
      this.newGuest.addressCityCode = spainCity?.cityCode || null;
      this.newGuest.addressCityName = spainCity?.cityName || null;
    },
  }
};
</script>

<style lang="scss">
#guest-form {
  .guest-signature {
    max-width: 250px;
  }
  #signature-pad {
    border: solid 1px #d8d6de;
    background-origin: border-box;
    background-clip: content-box, border-box;
    &.is-invalid {
      border: solid 1px #ea5455;
    }
  }
}
</style>
